import { Dispatch, SetStateAction, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IFailureCodeUnknown } from "entities/FailureDetailsEntity/FailureDetailsEntityInterfaces";
import { IEditionSecondaryCodes } from "../../hooks/useSecondaryCodeRequirementsColumnsInterface";
import { IEventReportRow } from "../../SecondaryCodeRequerimentsInterfaces";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable, FAILURE_CODE_EXCEPTIONS } from "constants/constants";
import { DropdownSecondaryCodeRequirements, IOptionSecondaryCodes } from "../Dropdown";
import { IFailureCode } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { UserContext } from "contexts/UserContext/UserContext";
import { CircularProgress, Typography } from "@mui/material";
import { EventReportsEntity } from "entities/EventReports/EventReportsEntity";

export const SecondaryFailureCodeDetailsDropdown = ({
    failureCodeUnknown,
    edit,
    setEdit,
    row,
}: {
    failureCodeUnknown: IFailureCodeUnknown[];
    edit: IEditionSecondaryCodes[];
    setEdit: Dispatch<SetStateAction<IEditionSecondaryCodes[]>>;
    row: IEventReportRow;
}) => {
    const { i18n, t } = useTranslation();
    const { user_id } = useContext(UserContext);

    const existingEdition = edit.findIndex((event) => event.id === row.event_report_id);
    const selectedSecondaryFailureCodeId = edit[existingEdition]?.changes?.secondary_failure_code_id;
    const selectedFailureCodeId = edit[existingEdition]?.changes?.failure_code_id;
    const selectedFunctionalLocationId = edit[existingEdition]?.changes?.functional_location_id;
    const { loading, secondaryFailureCodes } = EventReportsEntity.GetSecondaryFailureCodesForEdition(
        row.machine_center.machine_center_id,
        selectedFailureCodeId,
        selectedFunctionalLocationId,
    );

    const secondaryFailureCodesOptionsFormatted = useMemo(
        () =>
            secondaryFailureCodes.map((failureCode) => ({
                id: failureCode.failure_code_id,
                name: translateDefaultEntity(
                    i18n.language,
                    failureCode as unknown as IFailureCode,
                    "name",
                    DefaultTranslationFieldsInTable.FAILURE_CODE,
                ),
            })),
        [secondaryFailureCodes, i18n],
    );

    const secondaryFailureCodesOptions: IOptionSecondaryCodes[] = useMemo(
        () =>
            loading
                ? []
                : [
                      ...(secondaryFailureCodesOptionsFormatted.length
                          ? [
                                ...failureCodeUnknown.map((FailureCode) => ({
                                    id: FailureCode.failure_code_id,
                                    name: translateDefaultEntity(
                                        i18n.language,
                                        FailureCode,
                                        "name",
                                        DefaultTranslationFieldsInTable.FAILURE_CODE,
                                    ),
                                })),
                            ]
                          : []),
                      ...secondaryFailureCodesOptionsFormatted,
                  ],
        [loading, secondaryFailureCodesOptionsFormatted, i18n, failureCodeUnknown],
    );

    const handleSecodaryFailureCodeUpdate = async (id: number) => {
        if (existingEdition !== -1) {
            let editExisting = [...edit];
            editExisting[existingEdition] = {
                ...editExisting[existingEdition],
                changes: {
                    ...editExisting[existingEdition]?.changes,
                    secondary_failure_code_id: id,
                    ...(user_id && { edited_by_user_id: user_id }),
                },
            };
            setEdit(editExisting);
        }
    };

    return loading ? (
        <CircularProgress size={20} color="info" />
    ) : !secondaryFailureCodes.length ? (
        <Typography style={{ color: "#192D4E", fontSize: "14px", fontFamily: "Open Sans" }}>
            {t("locations.secondary_code_requirements_table.not_applicable")}
        </Typography>
    ) : (
        <DropdownSecondaryCodeRequirements
            optionSelected={
                row?.secondary_failure_code?.code === FAILURE_CODE_EXCEPTIONS.NOT_SELECTED &&
                selectedSecondaryFailureCodeId === row?.secondary_failure_code?.failure_code_id
                    ? undefined
                    : selectedSecondaryFailureCodeId
            }
            disabled={!selectedFailureCodeId}
            inlineStyle={{ width: "80px" }}
            options={secondaryFailureCodesOptions}
            handleUpdate={handleSecodaryFailureCodeUpdate}
        />
    );
};
